import { useCallback, useEffect, useState, type DragEvent } from "react";

import {
  api,
  type CalendarColor,
  type Label as LabelData,
  type Planner,
  type PlannerActivity,
  type PlannerDay,
  type Template,
  type TemplateLibrary,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApplyRegimenDialog } from "@/components/apply-regimen";
import { ErrorAlert } from "@/components/error-alert";
import { LibraryBoard } from "@/components/library-board";
import { PageSkeleton } from "@/components/page-skeleton";
import { DraggableTemplateCard } from "@/components/template-card";
import {
  TEMPLATE_DRAG_TYPE,
  type TemplateDragPayload,
} from "@/components/timeline";
import { formatMinutes, readableText } from "@/lib/color";
import { labelHex } from "@/lib/domain";
import { cn } from "@/lib/utils";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A Template dropped on a day, waiting for its time to be confirmed. */
type Pending = {
  date: string;
  template: Template;
  labelId: string;
  time: string;
};

/** Local Date for YYYY-MM-DD at HH:MM. */
function localAt(date: string, hhmm: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  const [h, min] = hhmm.split(":").map(Number);
  return new Date(y, m - 1, d, h || 0, min || 0, 0, 0);
}

export function PlannerPage() {
  const [planner, setPlanner] = useState<Planner | null>(null);
  const [library, setLibrary] = useState<TemplateLibrary | null>(null);
  const [labels, setLabels] = useState<LabelData[]>([]);
  const [colors, setColors] = useState<CalendarColor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  // Sunday is when next week gets planned, so the Planner opens on it then.
  const [week, setWeek] = useState<"current" | "next">(() =>
    new Date().getDay() === 0 ? "next" : "current",
  );

  const load = useCallback(async () => {
    try {
      const [p, lib, labelData] = await Promise.all([
        api.planner(week),
        api.templates(),
        api.labels(),
      ]);
      setPlanner(p);
      setLibrary(lib);
      setLabels(labelData.labels);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load the planner"));
    }
  }, [week]);

  useEffect(() => {
    void load();
    (async () => {
      try {
        setColors((await api.colors()).colors);
      } catch {
        // Colours are decoration.
      }
    })();
  }, [load]);

  function startPending(date: string, template: Template, labelId: string) {
    setActionError(null);
    setPending({
      date,
      template,
      labelId,
      time: template.suggested_start || "09:00",
    });
  }

  async function confirmPending() {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.scheduleActivity({
        template_id: pending.template.id,
        display_label_id: pending.labelId,
        start_at: localAt(pending.date, pending.time).toISOString(),
      });
      setPending(null);
      await load();
    } catch (err) {
      setActionError(messageOf(err, "could not schedule"));
    }
    setBusy(false);
  }

  async function remove(act: PlannerActivity) {
    if (
      !window.confirm(
        `Remove "${act.title}" from ${new Date(act.start_at).toLocaleDateString([], { weekday: "long" })}? It is deleted from Google Calendar too.`,
      )
    )
      return;
    setBusy(true);
    setActionError(null);
    try {
      await api.deleteActivity(act.id);
      await load();
    } catch (err) {
      setActionError(messageOf(err, "could not remove"));
    }
    setBusy(false);
  }

  if (error && !planner) return <ErrorAlert message={error} />;
  if (!planner || !library) return <PageSkeleton />;

  const templateById = new Map(library.templates.map((t) => [t.id, t]));
  const range = `${new Date(`${planner.start}T00:00`).toLocaleDateString([], { day: "numeric", month: "short" })} – ${new Date(`${planner.end}T00:00`).toLocaleDateString([], { day: "numeric", month: "short" })}`;

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      {actionError && <ErrorAlert message={actionError} />}

      <Card>
        <CardHeader>
          <CardTitle>Planner · {range}</CardTitle>
          <CardDescription>
            Lay out {week === "next" ? "next" : "this"} week. Drag a Template
            onto a day, or use a day&apos;s + to pick one; it lands at the
            Template&apos;s suggested time and you can adjust before it goes to
            Google Calendar. Existing appointments are shown faded. No capacity
            here; Today does that.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={week === "current" ? "default" : "outline"}
              onClick={() => setWeek("current")}
            >
              This week
            </Button>
            <Button
              type="button"
              size="sm"
              variant={week === "next" ? "default" : "outline"}
              onClick={() => setWeek("next")}
            >
              Next week
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => setApplying(true)}
            >
              Apply Regimen
            </Button>
          </div>
          <div className="grid gap-2 md:grid-cols-7">
            {planner.days.map((day) => (
              <DayColumn
                key={day.date}
                day={day}
                templates={library.templates}
                labels={labels}
                colors={colors}
                pending={pending?.date === day.date ? pending : null}
                busy={busy}
                onDrop={(payload) => {
                  const t = templateById.get(payload.template_id);
                  if (t) startPending(day.date, t, payload.label_id);
                }}
                onPick={(t) => startPending(day.date, t, t.label_ids[0] ?? "")}
                onPendingTime={(time) =>
                  setPending((p) => (p ? { ...p, time } : p))
                }
                onPendingConfirm={() => void confirmPending()}
                onPendingCancel={() => setPending(null)}
                onRemove={(act) => void remove(act)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {applying && (
        <ApplyRegimenDialog
          planner={planner}
          week={week}
          onClose={() => setApplying(false)}
          onApplied={async () => {
            setApplying(false);
            await load();
          }}
        />
      )}

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>Templates</CardTitle>
          <CardDescription>
            Drag onto a day above. The column you drag from sets the colour.
          </CardDescription>
        </CardHeader>
        <CardContent className="min-w-0">
          {labels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Create Labels and Templates first.
            </p>
          ) : (
            <LibraryBoard
              library={library}
              labels={labels}
              colors={colors}
              renderCard={(t, label, hex) => (
                <DraggableTemplateCard
                  template={t}
                  labelId={label.id}
                  accent={hex}
                  disabled={busy}
                  title="Drag onto a day"
                />
              )}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DayColumn({
  day,
  templates,
  labels,
  colors,
  pending,
  busy,
  onDrop,
  onPick,
  onPendingTime,
  onPendingConfirm,
  onPendingCancel,
  onRemove,
}: {
  day: PlannerDay;
  templates: Template[];
  labels: LabelData[];
  colors: CalendarColor[];
  pending: Pending | null;
  busy: boolean;
  onDrop: (payload: TemplateDragPayload) => void;
  onPick: (t: Template) => void;
  onPendingTime: (time: string) => void;
  onPendingConfirm: () => void;
  onPendingCancel: () => void;
  onRemove: (act: PlannerActivity) => void;
}) {
  const [over, setOver] = useState(false);
  const [picking, setPicking] = useState(false);
  const accepts = !day.is_past;
  const date = new Date(`${day.date}T00:00`);
  const labelById = new Map(labels.map((l) => [l.id, l]));

  function handleDragOver(ev: DragEvent<HTMLDivElement>) {
    if (!accepts || !ev.dataTransfer.types.includes(TEMPLATE_DRAG_TYPE)) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "copy";
    setOver(true);
  }
  function handleDrop(ev: DragEvent<HTMLDivElement>) {
    setOver(false);
    if (!accepts) return;
    const raw = ev.dataTransfer.getData(TEMPLATE_DRAG_TYPE);
    if (!raw) return;
    ev.preventDefault();
    onDrop(JSON.parse(raw) as TemplateDragPayload);
  }

  const pendingHex = pending
    ? (() => {
        const l = labelById.get(pending.labelId);
        return l ? labelHex(colors, l) : undefined;
      })()
    : undefined;

  return (
    <section
      aria-label={date.toLocaleDateString([], {
        weekday: "long",
        day: "numeric",
        month: "long",
      })}
      onDragOver={handleDragOver}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      className={cn(
        "flex min-h-48 min-w-0 flex-col rounded-xl border bg-secondary/30",
        day.is_today ? "border-primary" : "border-border",
        day.is_past && "opacity-70",
        over && "ring-2 ring-ring",
      )}
    >
      <header className="flex items-center justify-between gap-1 border-b border-border px-2.5 py-1.5">
        <div className="min-w-0">
          <p
            className={cn(
              "text-xs uppercase tracking-wide",
              day.is_today ? "text-primary" : "text-muted-foreground",
            )}
          >
            {date.toLocaleDateString([], { weekday: "short" })}
          </p>
          <p className="text-sm font-semibold leading-tight">
            {date.getDate()}
          </p>
        </div>
        {accepts && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 px-0"
            aria-label="Add a Template to this day"
            onClick={() => setPicking((p) => !p)}
            disabled={busy}
          >
            +
          </Button>
        )}
      </header>

      {picking && accepts && (
        <div className="border-b border-border p-2">
          <Select
            onValueChange={(id) => {
              const t = templates.find((x) => x.id === id);
              setPicking(false);
              if (t) onPick(t);
            }}
          >
            <SelectTrigger aria-label="Template" className="h-8 text-xs">
              <SelectValue placeholder="Pick a Template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <ul className="flex-1 space-y-1.5 p-2">
        {day.activities.length === 0 && !pending && (
          <li className="px-1 py-2 text-center text-xs text-muted-foreground">
            {day.is_past ? "" : accepts ? "Drop a Template here" : ""}
          </li>
        )}
        {day.activities.map((act) => {
          const text = readableText(act.color_hex);
          return (
            <li
              key={act.id}
              className={cn(
                "group relative rounded-md px-2 py-1.5 text-xs leading-tight shadow-sm",
                act.external &&
                  "border border-dashed border-foreground/40 shadow-none",
                !act.removable && !act.external && "opacity-75",
              )}
              style={{
                backgroundColor: act.color_hex || "var(--muted-foreground)",
                color: text,
                opacity: act.external ? 0.5 : undefined,
              }}
              title={`${act.title} ${clock(act.start_at)}–${clock(act.end_at)}${act.removable ? "" : " · change on Google Calendar"}`}
            >
              <span className="block truncate font-medium">
                {act.title || "(untitled)"}
              </span>
              <span className="block opacity-85">{clock(act.start_at)}</span>
              {act.removable && (
                <button
                  type="button"
                  aria-label={`Remove ${act.title}`}
                  onClick={() => onRemove(act)}
                  disabled={busy}
                  className="absolute right-1 top-1 hidden h-5 w-5 items-center justify-center rounded-sm bg-black/15 hover:bg-black/30 group-hover:flex"
                  style={{ color: text }}
                >
                  ×
                </button>
              )}
            </li>
          );
        })}
        {pending && (
          <li
            className="rounded-md border-2 border-dashed border-primary bg-card p-2 text-xs"
            style={pendingHex ? { borderColor: pendingHex } : undefined}
          >
            <p className="truncate font-medium">{pending.template.title}</p>
            <p className="text-muted-foreground">
              {formatMinutes(
                pending.template.type === "project"
                  ? 30
                  : pending.template.duration_minutes,
              )}
            </p>
            <div className="mt-1.5 flex items-center gap-1">
              <Input
                type="time"
                aria-label="Start time"
                className="h-7 flex-1 px-1.5 text-xs"
                value={pending.time}
                onChange={(e) => onPendingTime(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") onPendingConfirm();
                  if (e.key === "Escape") onPendingCancel();
                }}
              />
            </div>
            <div className="mt-1.5 flex gap-1">
              <Button
                size="sm"
                className="h-7 flex-1 px-2 text-xs"
                onClick={onPendingConfirm}
                disabled={busy}
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={onPendingCancel}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </li>
        )}
      </ul>
    </section>
  );
}
